/**
 * Trading Hours Validation Utility
 * 
 * Validates if current time is within Indian stock market trading hours
 * Trading Days: Monday to Friday (excluding public holidays)
 * Trading Hours: 9:15 AM to 3:30 PM IST
 */

/**
 * List of Indian stock market holidays for 2024-2025
 * Update this list annually
 */
const marketHolidays = [
  // 2024 Holidays
  '2024-01-26', // Republic Day
  '2024-03-08', // Maha Shivaratri
  '2024-03-25', // Holi
  '2024-03-29', // Good Friday
  '2024-04-11', // Id-Ul-Fitr (Ramadan Eid)
  '2024-04-17', // Ram Navami
  '2024-04-21', // Mahavir Jayanti
  '2024-05-01', // Maharashtra Day
  '2024-05-23', // Buddha Pournima
  '2024-06-17', // Bakri Eid
  '2024-07-17', // Muharram
  '2024-08-15', // Independence Day
  '2024-08-26', // Janmashtami
  '2024-10-02', // Mahatma Gandhi Jayanti
  '2024-10-12', // Dussehra
  '2024-11-01', // Diwali/Laxmi Pujan
  '2024-11-02', // Diwali (Balipratipada)
  '2024-11-15', // Gurunanak Jayanti
  '2024-12-25', // Christmas
  
  // 2025 Holidays (add as they're announced)
  '2025-01-26', // Republic Day
  '2025-03-14', // Holi
  '2025-03-31', // Id-Ul-Fitr
  '2025-04-10', // Mahavir Jayanti
  '2025-04-14', // Dr. Ambedkar Jayanti
  '2025-04-18', // Good Friday
  '2025-05-01', // Maharashtra Day
  '2025-06-06', // Bakri Eid
  '2025-08-15', // Independence Day
  '2025-08-16', // Parsi New Year
  '2025-08-27', // Ganesh Chaturthi
  '2025-10-02', // Mahatma Gandhi Jayanti
  '2025-10-21', // Dussehra
  '2025-10-22', // Diwali/Laxmi Pujan
  '2025-11-05', // Gurunanak Jayanti
  '2025-12-25', // Christmas
];

/**
 * Check if a date is a market holiday
 * @param {Date} date - Date to check
 * @returns {boolean} - True if market holiday
 */
export const isMarketHoliday = (date) => {
  const dateString = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  return marketHolidays.includes(dateString);
};

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * @param {Date} date - Date to check
 * @returns {boolean} - True if weekend
 */
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

/**
 * Check if current time is within trading hours (9:15 AM to 3:30 PM IST)
 * @param {Date} date - Date to check
 * @returns {boolean} - True if within trading hours
 */
export const isWithinTradingHours = (date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  
  // Market opens at 9:15 AM (09:15)
  const marketOpen = hours > 9 || (hours === 9 && minutes >= 15);
  
  // Market closes at 3:30 PM (15:30)
  const marketClose = hours < 15 || (hours === 15 && minutes <= 30);
  
  return marketOpen && marketClose;
};

/**
 * Get current date/time in IST (Indian Standard Time)
 * @returns {Date} - Current date in IST
 */
export const getCurrentISTDate = () => {
  // Create date in UTC
  const now = new Date();
  
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
  const istTime = new Date(utcTime + istOffset);
  
  return istTime;
};

/**
 * Check if it's a trading day
 * @param {Date} date - Date to check
 * @returns {boolean} - True if trading day
 */
export const isTradingDay = (date) => {
  return !isWeekend(date) && !isMarketHoliday(date);
};

/**
 * Comprehensive check: Is market open right now?
 * @returns {Object} - { isOpen: boolean, reason: string }
 */
export const isMarketOpen = () => {
  const now = getCurrentISTDate();
  
  // Check if weekend
  if (isWeekend(now)) {
    return {
      isOpen: false,
      reason: 'weekend',
      message: 'Market is closed on weekends (Saturday & Sunday)'
    };
  }
  
  // Check if holiday
  if (isMarketHoliday(now)) {
    return {
      isOpen: false,
      reason: 'holiday',
      message: 'Market is closed today due to a public holiday'
    };
  }
  
  // Check if within trading hours
  if (!isWithinTradingHours(now)) {
    const hours = now.getHours();
    const minutes = now.getMinutes();
    
    // Before market opens
    if (hours < 9 || (hours === 9 && minutes < 15)) {
      return {
        isOpen: false,
        reason: 'before_hours',
        message: 'Market opens at 9:15 AM IST'
      };
    }
    
    // After market closes
    return {
      isOpen: false,
      reason: 'after_hours',
      message: 'Market closed at 3:30 PM IST'
    };
  }
  
  // Market is open!
  return {
    isOpen: true,
    reason: 'open',
    message: 'Market is currently open for trading'
  };
};

/**
 * Get next market opening time
 * @returns {Object} - { date: Date, message: string }
 */
export const getNextMarketOpening = () => {
  const now = getCurrentISTDate();
  let nextOpening = new Date(now);
  
  // If it's after market hours today, move to next day
  if (now.getHours() >= 15 && now.getMinutes() > 30) {
    nextOpening.setDate(nextOpening.getDate() + 1);
  }
  
  // Skip weekends
  while (isWeekend(nextOpening)) {
    nextOpening.setDate(nextOpening.getDate() + 1);
  }
  
  // Skip holidays
  while (isMarketHoliday(nextOpening)) {
    nextOpening.setDate(nextOpening.getDate() + 1);
    
    // Also skip if the next day is a weekend
    while (isWeekend(nextOpening)) {
      nextOpening.setDate(nextOpening.getDate() + 1);
    }
  }
  
  // Set to market opening time (9:15 AM)
  nextOpening.setHours(9, 15, 0, 0);
  
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = daysOfWeek[nextOpening.getDay()];
  
  return {
    date: nextOpening,
    message: `Next market opens on ${dayName}, ${nextOpening.toLocaleDateString()} at 9:15 AM IST`
  };
};

/**
 * Format trading hours for display
 * @returns {string} - Formatted trading hours string
 */
export const getTradingHoursDisplay = () => {
  return 'Monday to Friday, 9:15 AM - 3:30 PM IST';
};

